import React, { useState, useEffect } from "react";
import appConfig, { getPortalKey } from "../config";
import BUILD_DATE from "../config/version";
import { requestResetToken, updatePassword } from "../utils/auth";

const ForgotPassword = ({ onBack }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [logonId, setLogonId] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Initialize config
  useEffect(() => {
    const key = getPortalKey();
    setConfig(appConfig[key] || appConfig.portal);
  }, []);

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);
    try {
      const result = await requestResetToken(email, logonId);
      if (result.success) {
        setStep(2);
      } else {
        setError(result.message || "No account found for that email and logon ID.");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const result = await updatePassword(token, newPassword, confirmPassword);
      if (result.success) {
        setSuccessMessage(result.message || "Your password has been updated.");
      } else {
        setError(result.message || "Failed to update password.");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Don't render until config is loaded
  if (!config.logo) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary to-background">
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  const showStep2Form = step === 2 && !successMessage;

  return (
    <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-br from-primary to-background px-4 py-8">
      <div className="bg-white p-6 md:p-8 rounded-2xl shadow-xl w-full max-w-md flex flex-col justify-center">

        {/* Logo - matching Login component */}
        <div className="flex flex-col items-center mb-6">
          <img
            src={config.logo}
            alt="Logo"
            className="h-16 mb-4 transition-transform duration-300 hover:scale-105"
          />
        </div>

        {/* Title */}
        <h1 className="text-center text-xl sm:text-2xl font-bold text-gray-800 mb-4">Forgot Password</h1>

        {/* Step 1: Email + Logon ID */}
        {step === 1 && (
          <>
            <p className="text-center text-gray-600 mb-6 text-sm sm:text-base">
              Enter your email address and logon ID below. We'll send you a reset token.
            </p>
            <form onSubmit={handleStep1Submit}>
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address<span className="text-red-500">*</span></label>
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary outline-none transition-all"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Logon ID<span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="Enter your logon ID"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary outline-none transition-all"
                  value={logonId}
                  onChange={(e) => setLogonId(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white py-3.5 rounded-lg font-semibold hover:bg-secondary transition-all duration-300 shadow-md hover:shadow-lg active:scale-[98%] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Sending...
                  </span>
                ) : (
                  "Send Reset Token"
                )}
              </button>
            </form>
          </>
        )}

        {/* Step 2: Reset token + new password */}
        {showStep2Form && (
          <>
            <p className="text-center text-gray-600 mb-6 text-sm sm:text-base">
              Enter the reset token from your email and choose a new password.
            </p>
            <form onSubmit={handleStep2Submit}>
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Reset Token<span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="Enter the reset token from your email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary outline-none transition-all"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password<span className="text-red-500">*</span></label>
                <input
                  type="password"
                  placeholder="Enter new password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary outline-none transition-all"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password<span className="text-red-500">*</span></label>
                <input
                  type="password"
                  placeholder="Confirm new password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary outline-none transition-all"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white py-3.5 rounded-lg font-semibold hover:bg-secondary transition-all duration-300 shadow-md hover:shadow-lg active:scale-[98%] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Updating...
                  </span>
                ) : (
                  "Update Password"
                )}
              </button>
            </form>
          </>
        )}

        {/* Success message with link to login */}
        {successMessage && (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
              {successMessage}
            </div>
            <div className="text-center">
              <button
                type="button"
                onClick={onBack}
                className="text-primary font-semibold underline hover:text-secondary transition-colors"
              >
                Return to Login
              </button>
            </div>
          </div>
        )}

        {/* Back to Login - shown in step 1 or during step 2 form (before success) */}
        {(step === 1 || showStep2Form) && (
          <div className="text-center mt-6">
            <button
              type="button"
              onClick={onBack}
              className="text-primary font-semibold underline hover:text-secondary transition-colors"
            >
              Back to Login
            </button>
          </div>
        )}
      </div>

      {/* Footer matching Login component */}
      <footer className="text-center text-sm text-white mt-8">
        <p>TribeVue® Portal v{BUILD_DATE}</p>
        <p>© 2026 Tribal D, Inc.</p>
      </footer>
    </div>
  );
};

export default ForgotPassword;
